/**
 * Live mode's look, one model for the phone and the web
 * (docs/plans/os-voice-conversation.md, The states): from what the server
 * says (`state`, `open.duplex`) and what the client knows (mute, playback,
 * the connection, the mic, the echo detector, Quiet, a loud room, a phone
 * call), the state, its caption words, the orb, the ripple, the chip asking
 * for an action, the other chips, and the sound and haptic its entry plays.
 *
 * Precedence, first wins: ended > mic refused > connecting > reconnecting >
 * interrupted > muted > needs tap > still there > roll-over > quiet > stopped
 * > speaking > working > listening. The web says "Needs a click" and "click
 * the orb", and plays no haptics.
 *
 * Words are i18n keys, never text: the client words them.
 */
import type { LiveDuplexMode, LivePlatform, LiveServerState } from '../agent-live-wire';
import { saveWordsKeyOf, type SaveWordsLanguage } from './save-language';

export type LiveStateName =
  | 'ended'
  | 'mic_blocked'
  | 'connecting'
  | 'reconnecting'
  | 'interrupted'
  | 'muted'
  | 'needs_tap'
  | 'still_there'
  | 'rollover'
  | 'quiet'
  | 'stopped'
  | 'speaking'
  | 'working'
  | 'listening';

/** The orb's mode, as `ORB_MODE` names it. */
export type LiveOrbMode = 'idle' | 'listening' | 'thinking' | 'speaking' | 'quiet';

export type LiveOrb = {
  readonly mode: LiveOrbMode;
  /** Opacity, 0–1. */
  readonly opacity: number;
  /** Scale against the orb's rest. */
  readonly scale: number;
  /** The blue glass halo of Needs your tap. */
  readonly halo: boolean;
  /**
   * The conversation window is open while Listening (option B, 7 Oct 2026):
   * a faint blue ring round the orb says a follow-up needs no "OS". No words.
   */
  readonly ring?: true;
};

/**
 * The voice in the pill, the dot grid (variation G, 7 Oct 2026; the name is
 * the ripple's it replaced): `level` follows the level the client writes —
 * the mic, and OS's playback while it speaks, half duplex too; `working`
 * follows it as well, with a small pulse from the centre about once a second,
 * so OS at work never looks like quiet listening (12 Oct 2026); `flat` is the
 * centre row, still; `muted` is the grey centre row; `pending` is a dim
 * centre row a band passes along (connecting, reconnecting). The grid draws
 * no rest: silent is the centre row alone, so `rippleRest` no longer moves it.
 */
export type LiveRipple = 'flat' | 'level' | 'working' | 'muted' | 'pending';

export type LiveAskAction = 'resume' | 'interrupt' | 'im_here' | 'carry_on';

/**
 * The chips beside the caption: Quiet with its ×, Room is loud · Quiet?, the
 * earphones tip of half duplex ("Voice can't interrupt while OS talks.
 * Earphones fix this."), the earphones note once they brought full duplex
 * back ("Earphones on. Talk over me any time."), the first-live mute tip, and
 * the dimmed "Was that for me?" after an utterance ended unsure (option B,
 * step 5), whose tap claims it.
 */
export type LiveChip = 'quiet' | 'loud' | 'earphones' | 'earphones_on' | 'mute_tip' | 'claim';

export type LiveSound = 'chime_rise' | 'chime_fall' | 'stop_tick' | 'mute_click' | 'tick_fall' | 'prompt' | 'chime_soft';
export type LiveHaptic = 'light' | 'soft' | 'warning' | 'success';

export type LiveDetail = { readonly key: string; readonly count?: number };

export type LiveLook = {
  readonly state: LiveStateName;
  /** The caption's word, an i18n key. */
  readonly word: string;
  /** The caption's detail after "·", an i18n key (and its count). */
  readonly detail?: LiveDetail;
  readonly orb: LiveOrb;
  readonly ripple: LiveRipple;
  /** Where the ripple rests with nobody speaking, 0–1. */
  readonly rippleRest: number;
  readonly askAction?: LiveAskAction;
  readonly chips: readonly LiveChip[];
  /** Mute drawn at 40%: it cannot act (a phone call holds the audio). */
  readonly muteDim: boolean;
  /** Played once on entering the state. */
  readonly cues: { readonly sound?: LiveSound; readonly haptic?: LiveHaptic };
};

export type LiveConnection = 'connecting' | 'open' | 'reconnecting' | 'closed';

export type LiveStateInput = {
  readonly platform: LivePlatform;
  readonly connection: LiveConnection;
  /** The server's last `state`, or null before one came. */
  readonly server: LiveServerState | null;
  /** The call's duplex from `open`, or null before it. */
  readonly duplex: LiveDuplexMode | null;
  readonly muted: boolean;
  /** OS's voice is playing here. */
  readonly playing: boolean;
  /** The person talks while OS plays: the voice is ducked. */
  readonly ducked: boolean;
  readonly mic: 'granted' | 'denied' | 'unknown';
  /** The self-echo detector fired: the call runs half duplex. */
  readonly echo: boolean;
  readonly quiet: boolean;
  readonly loud: boolean;
  /** A phone call or the system holds the audio. */
  readonly interrupted: boolean;
  /** Still there? and the roll-over count down from the server's `endsInMs`. */
  readonly endsInMs?: number;
  /** The earphones tip was not shown yet this call. */
  readonly earphonesTip?: boolean;
  /** Earphones just brought the call back to full duplex: their note shows. */
  readonly earphonesNote?: boolean;
  /** The first live session on this device: the "Tap to mute" tip. */
  readonly muteTip?: boolean;
  /** The language the person speaks to OS: Needs your tap names the save words in it (`spokenLanguageOf`); English when absent. */
  readonly saveLanguage?: SaveWordsLanguage;
  /** The server's `name_hint` came in the last `LIVE_NAME_HINT_SHOW_MS`: Listening says how to be heard. */
  readonly nameHint?: boolean;
  /** The server's last `state` said the conversation window is open: Listening rings the orb. */
  readonly windowOpen?: boolean;
  /** The server's `unsure` came in the last `LIVE_CLAIM_SHOW_MS`: the "Was that for me?" chip, while Listening or Working. */
  readonly claim?: boolean;
};

/** How long the caption says "say “OS” to ask" after the server's `name_hint`. */
export const LIVE_NAME_HINT_SHOW_MS = 4_000;

/** How long the dimmed "Was that for me?" chip shows after the server's `unsure` (option B, step 5). */
export const LIVE_CLAIM_SHOW_MS = 5_000;

const orb = (mode: LiveOrbMode, opacity = 1, scale = 1, halo = false): LiveOrb => ({ mode, opacity, scale, halo });

/** The connecting and reconnecting orb's opacity. */
export const LIVE_DIM_ORB = 0.45;
/** The speaking orb's scale. */
export const LIVE_SPEAKING_SCALE = 1.2;

const minutesLeft = (endsInMs: number | undefined): number => Math.max(1, Math.ceil((endsInMs ?? 0) / 60_000));

/** The state by precedence, before its look. */
export const liveStateNameOf = (input: LiveStateInput): LiveStateName => {
  if (input.connection === 'closed') return 'ended';
  if (input.mic === 'denied') return 'mic_blocked';
  if (input.connection === 'connecting') return 'connecting';
  if (input.connection === 'reconnecting' || input.server === 'reconnecting') return 'reconnecting';
  if (input.interrupted) return 'interrupted';
  if (input.muted) return 'muted';
  if (input.server === 'needs_tap') return 'needs_tap';
  if (input.server === 'still_there') return 'still_there';
  if (input.server === 'rollover') return 'rollover';
  if (input.quiet || input.server === 'quiet') return 'quiet';
  if (input.server === 'parked') return 'stopped';
  if (input.playing || input.server === 'speaking') return 'speaking';
  if (input.server === 'working') return 'working';
  return 'listening';
};

/** Whether the call runs client-gated half duplex: the server said so, or the echo detector fired. */
export const isHalfDuplex = (input: Pick<LiveStateInput, 'duplex' | 'echo'>): boolean => input.duplex === 'half' || input.echo;

/** Live mode's look now. */
export const liveStateOf = (input: LiveStateInput): LiveLook => {
  const web = input.platform === 'web';
  const state = liveStateNameOf(input);
  const extra: LiveChip[] = [];
  if (input.loud && state !== 'quiet' && state !== 'muted' && state !== 'ended' && state !== 'mic_blocked') extra.push('loud');
  if (input.muteTip === true && (state === 'listening' || state === 'speaking' || state === 'working')) extra.push('mute_tip');
  if (input.earphonesNote === true && !isHalfDuplex(input) && (state === 'listening' || state === 'speaking' || state === 'working')) extra.push('earphones_on');
  // Never while OS speaks, never in Quiet: only where the person could have been talking to OS (option B, step 5).
  if (input.claim === true && (state === 'listening' || state === 'working')) extra.push('claim');
  const look = (base: Omit<LiveLook, 'state' | 'chips' | 'muteDim' | 'cues'> & Partial<Pick<LiveLook, 'chips' | 'muteDim' | 'cues'>>): LiveLook => {
    const cues = base.cues ?? {};
    return {
      state,
      ...base,
      chips: [...(base.chips ?? []), ...extra],
      muteDim: base.muteDim ?? false,
      cues: web ? (cues.sound === undefined ? {} : { sound: cues.sound }) : cues,
    };
  };
  switch (state) {
    case 'ended':
      return look({ word: 'agent.live.ended_divider', orb: orb('idle'), ripple: 'flat', rippleRest: 0, cues: { sound: 'chime_fall', haptic: 'light' } });
    case 'mic_blocked':
      return look({
        word: web ? 'agent.live.mic_blocked.title' : 'agent.voice.mic_off_title',
        orb: orb('idle', LIVE_DIM_ORB),
        ripple: 'muted',
        rippleRest: 0,
      });
    case 'connecting':
      return look({ word: 'agent.live.connecting', orb: orb('idle', LIVE_DIM_ORB), ripple: 'pending', rippleRest: 0, cues: { haptic: 'light' } });
    case 'reconnecting':
      return look({
        word: 'agent.live.state.reconnecting',
        detail: { key: web ? 'agent.live.detail.typing_works_web' : 'agent.live.detail.typing_works' },
        orb: orb('idle', LIVE_DIM_ORB),
        ripple: 'pending',
        rippleRest: 0,
      });
    case 'interrupted':
      return look({
        word: 'agent.live.state.interrupted',
        detail: { key: 'agent.live.detail.phone_call' },
        orb: orb('quiet'),
        ripple: 'flat',
        rippleRest: 0,
        askAction: 'resume',
        muteDim: true,
      });
    case 'muted':
      return look({
        word: 'agent.live.muted',
        detail: { key: 'agent.live.detail.nothing_sent' },
        orb: orb('quiet'),
        ripple: 'muted',
        rippleRest: 0,
        cues: { sound: 'mute_click', haptic: 'light' },
      });
    case 'needs_tap':
      return look({
        word: web ? 'agent.live.state.needs_click' : 'agent.live.state.needs_tap',
        detail: { key: saveWordsKeyOf('agent.live.detail.tap_or_say', input.saveLanguage ?? 'en') },
        orb: orb('idle', 1, 1, true),
        ripple: 'level',
        rippleRest: 0.18,
        cues: { sound: 'prompt', haptic: 'warning' },
      });
    case 'still_there':
      return look({
        word: 'agent.live.state.still_there',
        detail: { key: 'agent.live.detail.ends_in', count: minutesLeft(input.endsInMs) },
        orb: orb('idle'),
        ripple: 'level',
        rippleRest: 0.05,
        askAction: 'im_here',
        cues: { sound: 'chime_soft', haptic: 'light' },
      });
    case 'rollover':
      return look({
        word: 'agent.live.chip.rollover',
        orb: orb('listening'),
        ripple: 'level',
        rippleRest: 0.18,
        askAction: 'carry_on',
        cues: { sound: 'chime_soft', haptic: 'light' },
      });
    case 'quiet':
      return look({
        word: 'agent.live.state.quiet',
        detail: { key: web ? 'agent.live.detail.click_orb' : 'agent.live.detail.call_me' },
        orb: orb('quiet'),
        ripple: 'level',
        rippleRest: 0.18,
        chips: ['quiet'],
        cues: { sound: 'tick_fall', haptic: 'light' },
      });
    case 'stopped':
      return look({
        word: 'agent.live.stopped',
        detail: { key: 'agent.live.detail.say_bolo' },
        orb: orb('idle'),
        ripple: 'level',
        rippleRest: 0.18,
        askAction: 'resume',
        cues: { sound: 'stop_tick', haptic: 'light' },
      });
    case 'speaking':
      return isHalfDuplex(input)
        ? look({
            word: 'agent.live.state.speaking',
            orb: orb('speaking'),
            // OS's own voice drives the grid while it speaks, though the mic is not sending.
            ripple: 'level',
            rippleRest: 0,
            askAction: 'interrupt',
            chips: input.earphonesTip === true ? ['earphones'] : [],
            cues: { haptic: 'light' },
          })
        : look({
            word: 'agent.live.state.speaking',
            ...(input.ducked ? { detail: { key: 'agent.live.detail.ducked' } } : {}),
            orb: orb('speaking', 1, LIVE_SPEAKING_SCALE),
            ripple: 'level',
            rippleRest: 0.12,
          });
    case 'working':
      return look({ word: 'agent.live.state.working', orb: orb('thinking'), ripple: 'working', rippleRest: 0.18, cues: { haptic: 'soft' } });
    case 'listening':
      return look({
        word: 'agent.live.state.listening',
        // Speech judged not for OS twice running, after the person spoke to it: how to be heard (7 Oct evening, item 4).
        ...(input.nameHint === true ? { detail: { key: 'agent.live.detail.say_name' } } : {}),
        // The conversation window is open: a follow-up needs no "OS" (option B, step 2).
        orb: input.windowOpen === true ? { ...orb('listening'), ring: true } : orb('listening'),
        ripple: 'level',
        rippleRest: 0,
        cues: { sound: 'chime_rise' },
      });
  }
};
